import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import {
    PiArrowRight,
    PiCalendarBlank,
    PiCheckCircle,
    PiCircle,
    PiClock,
    PiInfo,
    PiMagnifyingGlass,
    PiUser,
    PiWarningCircle,
    PiXCircle
} from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { Badge, Button, Input, Text, Title } from 'rizzui';
import useFetch from '../../../../../hooks/useFetch';
import { LeadsService } from '../../../../../services/leads/leadsService';
import { LeadStatusHistory as LeadStatusHistoryType } from '../../../../../type/entities/leads-type';
import LeadProfileLayout from '../../LeadProfileLayout';

export default function LeadStatusHistory() {
    const { id = '' } = useParams<{ id: string }>();
    const service = new LeadsService();

    // State management for filters and search functionality
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTimeRange, setSelectedTimeRange] = useState('all');

    const [data, isLoading] = useFetch(useCallback(async () => {
        if (!id || id === '') return [];
        const response = await service.getLeadStatusesHistory(id);
        return response.getResponseData() as LeadStatusHistoryType[];
    }, [id]));

    /**
     * Returns the appropriate icon component based on status name
     * @param statusName - The name of the status to get icon for
     * @returns React icon component representing the status type
     */
    const getStatusIcon = (statusName: string) => {
        const name = statusName?.toLowerCase();
        if (name?.includes('completado') || name?.includes('cerrado') || name?.includes('ganado')) {
            return <PiCheckCircle className="h-4 w-4" />;
        }
        if (name?.includes('cancelado') || name?.includes('perdido') || name?.includes('rechazado')) {
            return <PiXCircle className="h-4 w-4" />;
        }
        if (name?.includes('pendiente') || name?.includes('espera')) {
            return <PiWarningCircle className="h-4 w-4" />;
        }
        return <PiCircle className="h-4 w-4" />;
    };

    /**
     * Calculates the relative luminance of a color using WCAG standards
     * @param color - Hexadecimal color string (e.g., "#ff0000")
     * @returns Luminance value between 0 (darkest) and 1 (brightest)
     */
    const getLuminance = (color: string): number => {
        // Convert hex color to RGB values
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16) / 255;
        const g = parseInt(hex.substr(2, 2), 16) / 255;
        const b = parseInt(hex.substr(4, 2), 16) / 255;

        // Apply gamma correction for accurate luminance calculation
        const getRGB = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

        const rLinear = getRGB(r);
        const gLinear = getRGB(g);
        const bLinear = getRGB(b);

        // Calculate relative luminance using WCAG formula
        return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
    };

    /**
     * Calculates the contrast ratio between two colors according to WCAG guidelines
     * @param color1 - First color in hexadecimal format
     * @param color2 - Second color in hexadecimal format
     * @returns Contrast ratio value (1:1 to 21:1, where 21:1 is maximum contrast)
     */
    const getContrastRatio = (color1: string, color2: string): number => {
        const lum1 = getLuminance(color1);
        const lum2 = getLuminance(color2);
        const brightest = Math.max(lum1, lum2);
        const darkest = Math.min(lum1, lum2);
        return (brightest + 0.05) / (darkest + 0.05);
    };

    /**
     * Determines the optimal text color (white or black) for maximum contrast against a background
     * @param backgroundColor - Background color in hexadecimal format
     * @returns "#ffffff" for white text or "#000000" for black text
     */
    const getBestTextColor = (backgroundColor: string): string => {
        const whiteContrast = getContrastRatio(backgroundColor, '#ffffff');
        const blackContrast = getContrastRatio(backgroundColor, '#000000');

        // Return the color with better contrast (minimum WCAG AA standard: 4.5:1)
        return whiteContrast > blackContrast ? '#ffffff' : '#000000';
    };

    /**
     * Calculates optimal text color for badges with semi-transparent backgrounds
     * Simulates the actual rendered color by blending with white background
     * @param statusColor - The status color in hexadecimal format
     * @returns Optimal text color for maximum readability on semi-transparent badge
     */
    const getOptimalTextColorForBadge = (statusColor: string): string => {
        // Simulate the resulting color from semi-transparent background (15% opacity) over white
        const hex = statusColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Blend with white (255,255,255) using 15% opacity of status color
        const alpha = 0.15;
        const mixedR = Math.round(r * alpha + 255 * (1 - alpha));
        const mixedG = Math.round(g * alpha + 255 * (1 - alpha));
        const mixedB = Math.round(b * alpha + 255 * (1 - alpha));

        const mixedColor = `#${mixedR.toString(16).padStart(2, '0')}${mixedG.toString(16).padStart(2, '0')}${mixedB.toString(16).padStart(2, '0')}`;

        // Calculate which text color has the best contrast on this blended background
        const whiteContrast = getContrastRatio(mixedColor, '#ffffff');
        const blackContrast = getContrastRatio(mixedColor, '#000000');
        const statusColorContrast = getContrastRatio(mixedColor, statusColor);

        // Choose the color with best contrast, prioritizing status color if readable
        if (statusColorContrast >= 4.5) {
            return statusColor; // Status color is readable
        } else if (blackContrast > whiteContrast) {
            return '#000000'; // Black has better contrast
        } else {
            return statusColor; // Use status color as fallback for visual consistency
        }
    };

    /**
     * Returns the appropriate color for a status based on its name or uses provided color
     * @param statusName - Name of the status to determine color for
     * @param defaultColor - Optional custom color to use instead of default mapping
     * @returns Hexadecimal color string for the status
     */
    const getStatusColor = (statusName: string, defaultColor?: string) => {
        if (defaultColor) return defaultColor;

        const name = statusName?.toLowerCase();
        if (name?.includes('completado') || name?.includes('cerrado') || name?.includes('ganado')) {
            return '#10b981'; // green-500
        }
        if (name?.includes('cancelado') || name?.includes('perdido') || name?.includes('rechazado')) {
            return '#ef4444'; // red-500
        }
        if (name?.includes('pendiente') || name?.includes('espera')) {
            return '#f59e0b'; // amber-500
        }
        return '#6366f1'; // indigo-500
    };

    // Filter data based on search term and time range
    const filteredData = useMemo(() => {
        if (!data?.data) return [];

        let filtered = data.data;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter((item: LeadStatusHistoryType) =>
                item.statuses?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.creator?.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by time range
        if (selectedTimeRange !== 'all') {
            const now = moment();
            filtered = filtered.filter((item: LeadStatusHistoryType) => {
                const itemDate = moment(item.createdAt?.date);
                switch (selectedTimeRange) {
                    case 'today':
                        return itemDate.isSame(now, 'day');
                    case 'week':
                        return itemDate.isAfter(now.clone().subtract(7, 'days'));
                    case 'month':
                        return itemDate.isAfter(now.clone().subtract(30, 'days'));
                    default:
                        return true;
                }
            });
        }

        // Sort by date in descending order (newest first)
        filtered = filtered.sort((a: LeadStatusHistoryType, b: LeadStatusHistoryType) =>
            moment(b.createdAt?.date).valueOf() - moment(a.createdAt?.date).valueOf()
        );

        return filtered;
    }, [data?.data, searchTerm, selectedTimeRange]);

    // Calculate statistics for the status history
    const stats = useMemo(() => {
        if (!data?.data) return { total: 0, thisWeek: 0, thisMonth: 0 };

        const now = moment();
        const thisWeek = data.data.filter((item: LeadStatusHistoryType) =>
            moment(item.createdAt?.date).isAfter(now.clone().subtract(7, 'days'))
        ).length;
        const thisMonth = data.data.filter((item: LeadStatusHistoryType) =>
            moment(item.createdAt?.date).isAfter(now.clone().subtract(30, 'days'))
        ).length;

        return {
            total: data.data.length,
            thisWeek,
            thisMonth
        };
    }, [data?.data]);

    return (
        <LeadProfileLayout isLoading={isLoading}>
            <div className="space-y-6">
                {/* Filter and search controls */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="flex-1 max-w-md">
                            <Input
                                type="text"
                                placeholder="Buscar en historial..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                prefix={<PiMagnifyingGlass className="h-4 w-4 text-gray-400" />}
                                className="w-full"
                            />
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {[
                                { value: 'all', label: 'Todo' },
                                { value: 'today', label: 'Hoy' },
                                { value: 'week', label: 'Esta semana' },
                                { value: 'month', label: 'Este mes' }
                            ].map(option => (
                                <Button
                                    key={option.value}
                                    variant={selectedTimeRange === option.value ? "solid" : "outline"}
                                    size="sm"
                                    onClick={() => setSelectedTimeRange(option.value)}
                                    className="text-xs"
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Enhanced timeline */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    {filteredData && filteredData.length > 0 ? (
                        <div className="p-6">
                            <div className="relative">
                                {/* Vertical timeline line */}
                                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-purple-200 to-gray-200"></div>

                                <div className="space-y-6">
                                    {filteredData.map((item: LeadStatusHistoryType, index: number) => {
                                        const statusColor = getStatusColor(item.statuses?.name, item.statuses?.color);
                                        const textColor = getOptimalTextColorForBadge(statusColor);
                                        const isFirst = index === 0;
                                        const isLast = index === filteredData.length - 1;

                                        return (
                                            <div
                                                key={`timeline-${item.id || index}`}
                                                className={`relative flex items-start space-x-6 group hover:bg-gray-50 rounded-lg p-4 transition-all duration-200 ${isFirst ? 'bg-primary-50 border border-blue-200' : ''}`}
                                            >
                                                {/* Timeline icon */}
                                                <div className="relative flex-shrink-0">
                                                    <div
                                                        className={`flex items-center justify-center w-8 h-8 rounded-full border-2 border-white shadow-lg transition-transform duration-200 group-hover:scale-110 ${isFirst ? 'ring-2 ring-blue-300' : ''}`}
                                                        style={{ backgroundColor: statusColor }}
                                                    >
                                                        <div className="text-white">
                                                            {getStatusIcon(item.statuses?.name)}
                                                        </div>
                                                    </div>
                                                    {isFirst && (
                                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                                                    )}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center space-x-3">
                                                            <Badge
                                                                variant="flat"
                                                                className="font-semibold"
                                                                style={{
                                                                    backgroundColor: `${statusColor}15`,
                                                                    color: textColor,
                                                                    borderColor: `${statusColor}30`
                                                                }}
                                                            >
                                                                {item.statuses?.name}
                                                            </Badge>
                                                            {isFirst && (
                                                                <Badge variant="flat" color="success" className="text-xs">
                                                                    Estado Actual
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                                                            <PiCalendarBlank className="h-4 w-4" />
                                                            <span>{moment(item.createdAt?.date).format('DD/MM/YYYY')}</span>
                                                            <PiClock className="h-4 w-4 ml-2" />
                                                            <span>{moment(item.createdAt?.date).format('HH:mm')}</span>
                                                        </div>
                                                    </div>

                                                    {/* User information */}
                                                    {item.creator && (
                                                        <div className="flex items-center space-x-2 mb-3">
                                                            <PiUser className="h-4 w-4 text-gray-400" />
                                                            <Text className="text-sm font-medium text-gray-700">
                                                                {item.creator.name} {item.creator.lastName}
                                                            </Text>
                                                            <span className="text-gray-400">•</span>
                                                            <Text className="text-xs text-gray-500">
                                                                {moment(item.createdAt?.date).fromNow()}
                                                            </Text>
                                                        </div>
                                                    )}

                                                    {/* Comment */}
                                                    {item.comment && (
                                                        <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-gray-300">
                                                            <Text className="text-sm text-gray-700 leading-relaxed">
                                                                {item.comment}
                                                            </Text>
                                                        </div>
                                                    )}

                                                    {/* Transition indicator */}
                                                    {!isLast && (
                                                        <div className="flex items-center mt-4 text-xs text-gray-400">
                                                            <PiArrowRight className="h-3 w-3 mr-1" />
                                                            <span>Cambio de estado</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <div className="mx-auto h-16 w-16 text-gray-300 mb-4">
                                <PiInfo className="h-full w-full" />
                            </div>
                            <Title as="h3" className="text-lg font-medium text-gray-900 mb-2">
                                {searchTerm || selectedTimeRange !== 'all' ? 'No se encontraron resultados' : 'Sin historial de estados'}
                            </Title>
                            <Text className="text-gray-500 mb-4">
                                {searchTerm || selectedTimeRange !== 'all'
                                    ? 'Intenta ajustar los filtros de búsqueda'
                                    : 'No hay cambios de estado registrados para este lead'
                                }
                            </Text>
                            {(searchTerm || selectedTimeRange !== 'all') && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedTimeRange('all');
                                    }}
                                >
                                    Limpiar filtros
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </LeadProfileLayout>
    );
}